import React, { useState, useEffect } from 'react';
import {
  X, Edit, Trash2, Plus, CheckCircle, Circle, Clock,
  FileText, Upload, Calendar, MessageSquare, AlertCircle, User, Star, Link as LinkIcon, Activity
} from 'lucide-react';
import { useProjectsStore, Project, Task } from '../../store/projectsStore';
import { useAuthStore } from '../../store/authStore';
import { useTeamStore } from '../../store/teamStore';
import TaskDetailModal from './components/TaskDetailModal';
import AddMilestoneModal from './components/AddMilestoneModal';
import AssetLibraryPicker from './components/AssetLibraryPicker';
import TaskAssignmentModal from './components/TaskAssignmentModal';

interface ViewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

type TabType = 'tasks' | 'files' | 'timeline' | 'activity' | 'settings' | 'comments';

const ViewProjectModal: React.FC<ViewProjectModalProps> = ({ isOpen, onClose, project }) => {
  const {
    tasks,
    files,
    timelines,
    comments,
    projects,
    fetchTasks,
    fetchFiles,
    fetchTimelines,
    fetchComments,
    createTask,
    updateTask,
    deleteTask,
    uploadFile,
    deleteFile,
    deleteTimeline,
    createComment,
    deleteComment,
    deleteProject,
    updateProject
  } = useProjectsStore();
  const { user } = useAuthStore();
  const { teamMembers, fetchTeamMembers } = useTeamStore();

  const [activeTab, setActiveTab] = useState<TabType>('tasks');
  const [isEditing, setIsEditing] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newComment, setNewComment] = useState('');
  const [fileInput, setFileInput] = useState<HTMLInputElement | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [assetPickerContext, setAssetPickerContext] = useState<'file' | 'task' | null>(null);
  const [selectedTaskForAsset, setSelectedTaskForAsset] = useState<string | null>(null);
  const [taskForAssignment, setTaskForAssignment] = useState<Task | null>(null);

  useEffect(() => {
    if (isOpen && project) {
      fetchTasks(project.id);
      fetchFiles(project.id);
      fetchTimelines(project.id);
      fetchComments(project.id);
      fetchTeamMembers();
    }
  }, [isOpen, project, fetchTasks, fetchFiles, fetchTimelines, fetchComments, fetchTeamMembers]);

  if (!isOpen) return null;

  // Get the current project from the store to ensure reactivity
  const currentProject = projects.find(p => p.id === project.id) || project;

  const projectTasks = tasks.filter(t => t.projectId === currentProject.id);
  const projectFiles = files.filter(f => f.projectId === currentProject.id);
  const projectTimelines = timelines.filter(t => t.projectId === currentProject.id);
  const projectComments = comments.filter(c => c.projectId === currentProject.id);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatRelativeTime = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getAssignedUserName = (userId: string | undefined) => {
    if (!userId) return 'Unassigned';
    const member = teamMembers.find(m => m.id === userId);
    return member ? member.name : 'Unknown User';
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    await createTask({
      projectId: currentProject.id,
      title: newTaskTitle,
      priority: 'medium',
      status: 'todo',
      order: projectTasks.length
    });
    setNewTaskTitle('');
  };

  const handleLinkAsset = async (assetId: string, asset: any) => {
    if (assetPickerContext === 'file') {
      // Link asset to project file
      // This would require a new API endpoint to link assets to files
      console.log('Linking asset to file:', assetId, asset);
    } else if (assetPickerContext === 'task' && selectedTaskForAsset) {
      // Link asset to task
      const task = projectTasks.find(t => t.id === selectedTaskForAsset);
      if (task) {
        const currentAttachments = task.attachments || [];
        await updateTask({
          id: task.id,
          attachments: [
            ...currentAttachments,
            {
              assetId,
              fileName: asset.originalName,
              fileUrl: asset.cdnUrl,
              fileType: asset.mimeType,
              fileSize: asset.fileSize,
              attachedBy: user?.id || '',
              attachedAt: new Date().toISOString(),
            },
          ],
        });
      }
    }
    setShowAssetPicker(false);
    setAssetPickerContext(null);
    setSelectedTaskForAsset(null);
  };

  const handleToggleTask = async (task: Task) => {
    const newStatus = task.status === 'done' ? 'todo' : task.status === 'todo' ? 'in_progress' : 'done';
    await updateTask({ id: task.id, status: newStatus });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(currentProject.id, file);
      if (fileInput) fileInput.value = '';
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    await createComment({
      projectId: currentProject.id,
      text: newComment
    });
    setNewComment('');
  };

  const handleDeleteProject = async () => {
    if (confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      await deleteProject(currentProject.id);
      onClose();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'todo': return <Circle className="w-4 h-4 text-gray-400 dark:text-gray-500" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-primary-500" />;
      case 'in_review': return <FileText className="w-4 h-4 text-yellow-500" />;
      case 'blocked': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'done': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo': return 'text-gray-600 dark:text-gray-400';
      case 'in_progress': return 'text-primary-600 dark:text-primary-400';
      case 'in_review': return 'text-yellow-600 dark:text-yellow-400';
      case 'blocked': return 'text-red-600 dark:text-red-400';
      case 'done': return 'text-green-600 dark:text-green-400 line-through';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Star className="w-3 h-3 text-red-500 fill-red-500" />;
      case 'high': return <Star className="w-3 h-3 text-orange-500 fill-orange-500" />;
      case 'medium': return <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />;
      default: return <Star className="w-3 h-3 text-gray-400 dark:text-gray-500" />;
    }
  };

  const getStatusStyles = (status: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      draft: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
      active: { bg: 'bg-primary-100 dark:bg-primary-900/30', text: 'text-primary-700 dark:text-primary-400' },
      on_hold: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
      completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
      archived: { bg: 'bg-gray-200 dark:bg-gray-600', text: 'text-gray-700 dark:text-gray-300' },
    };
    return styles[status] || styles.draft;
  };

  const getPriorityStyles = (priority: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      urgent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
      high: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400' },
      medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
      low: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
    };
    return styles[priority] || styles.medium;
  };

  const getTaskCommentsCount = (taskId: string) => {
    return comments.filter(c => c.taskId === taskId).length;
  };

  // Calculate progress dynamically based on task completion
  const totalTasks = projectTasks.length;
  const completedTasks = projectTasks.filter(t => t.status === 'done').length;
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const statusStyle = getStatusStyles(currentProject.status);
  const priorityStyle = getPriorityStyles(currentProject.priority);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">{currentProject.title}</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                  {currentProject.status.replace('_', ' ').toUpperCase()}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                  {currentProject.priority.toUpperCase()}
                </span>
                {currentProject.deadline && (
                  <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="w-4 h-4" />
                    {formatDate(currentProject.deadline)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={handleDeleteProject}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Overall Progress</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-primary-500 to-indigo-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Description */}
          {currentProject.description && (
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">{currentProject.description}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-1 px-6 py-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            {(['tasks', 'files', 'timeline', 'activity', 'comments', 'settings'] as TabType[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize ${activeTab === tab
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tasks</h3>
                  {/* Only show add task input for non-contract projects */}
                  {!currentProject.contractId ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTask();
                          }
                        }}
                        placeholder="Add a new task..."
                        className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                      />
                      <button
                        onClick={handleAddTask}
                        className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5 rounded-lg">
                      <LinkIcon className="w-4 h-4" />
                      Synced with contract deliverables
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {projectTasks.map(task => (
                    <div
                      key={task.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600 cursor-pointer"
                      onClick={() => setSelectedTask(task)}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTask(task);
                          }}
                          className="flex-shrink-0 mt-0.5"
                        >
                          {getStatusIcon(task.status)}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className={`font-medium ${getStatusColor(task.status)}`}>
                              {task.title}
                            </p>
                            {getPriorityIcon(task.priority)}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTaskForAssignment(task);
                              }}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors ${task.assignedTo
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-900/50'
                                : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                                }`}
                            >
                              <User className="w-3 h-3" />
                              {task.assignedTo ? getAssignedUserName(task.assignedTo) : 'Assign'}
                            </button>
                            {task.dueDate && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Due: {formatDate(task.dueDate)}
                              </span>
                            )}
                            {task.attachments && task.attachments.length > 0 && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {task.attachments.length} file{task.attachments.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {getTaskCommentsCount(task.id) > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {getTaskCommentsCount(task.id)} comment{getTaskCommentsCount(task.id) !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTask(task.id);
                          }}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {projectTasks.length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">No tasks yet. Add your first task above.</p>
                  )}
                </div>
              </div>
            )}

            {/* Files Tab */}
            {activeTab === 'files' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Files</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setAssetPickerContext('file');
                        setShowAssetPicker(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <LinkIcon className="w-4 h-4" />
                      Link from Asset Library
                    </button>
                    <label className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors cursor-pointer">
                      <Upload className="w-4 h-4" />
                      Upload File
                      <input
                        ref={setFileInput}
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  {projectFiles.map(file => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600"
                    >
                      <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{file.fileName}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(file.createdAt)}</p>
                      </div>
                      <a
                        href={file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        View
                      </a>
                      <button
                        onClick={() => deleteFile(file.id)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {projectFiles.length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">No files uploaded yet.</p>
                  )}
                </div>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Timeline</h3>
                  <button
                    onClick={() => setShowMilestoneModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Milestone
                  </button>
                </div>
                <div className="space-y-4">
                  {projectTimelines.map(timeline => (
                    <div
                      key={timeline.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">{timeline.milestone}</h4>
                          {timeline.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{timeline.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {timeline.status === 'completed' && (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{timeline.progress}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(timeline.startDate)} - {formatDate(timeline.endDate)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2">
                        <div
                          className={`h-2 rounded-full transition-all ${timeline.progress === 100 ? 'bg-green-600' : 'bg-primary-600'
                            }`}
                          style={{ width: `${timeline.progress}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => deleteTimeline(timeline.id)}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {projectTimelines.length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">No timeline milestones yet.</p>
                  )}
                </div>
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Activity</h3>
                </div>
                <div className="space-y-3">
                  {/* Recent activity from tasks, files, comments */}
                  {projectTasks.slice(0, 5).map(task => (
                    <div key={task.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <Activity className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          Task <span className="font-medium">"{task.title}"</span> was {task.status === 'done' ? 'completed' : `updated to ${task.status.replace('_', ' ')}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatRelativeTime(task.updatedAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                  {projectTasks.length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">No recent activity</p>
                  )}
                </div>
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Comments</h3>
                </div>
                <div className="space-y-4 mb-4 max-h-[60vh] overflow-y-auto">
                  {projectComments.length > 0 ? (
                    projectComments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {getAssignedUserName(comment.author)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatRelativeTime(comment.createdAt)}
                            </span>
                          </div>
                          {user?.id === comment.author && (
                            <button
                              onClick={() => deleteComment(comment.id)}
                              className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{comment.text}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">No comments yet</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                    placeholder="Add a comment..."
                    className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  />
                  <button
                    onClick={handleAddComment}
                    className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Project Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Project Title
                      </label>
                      <input
                        type="text"
                        value={currentProject.title}
                        readOnly
                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Description
                      </label>
                      <textarea
                        value={currentProject.description || ''}
                        readOnly
                        rows={4}
                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Status
                        </label>
                        <select
                          value={currentProject.status}
                          onChange={async (e) => {
                            await updateProject({
                              id: currentProject.id,
                              status: e.target.value as Project['status'],
                            });
                          }}
                          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        >
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="on_hold">On Hold</option>
                          <option value="completed">Completed</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Priority
                        </label>
                        <select
                          value={currentProject.priority}
                          onChange={async (e) => {
                            await updateProject({
                              id: currentProject.id,
                              priority: e.target.value as Project['priority'],
                            });
                          }}
                          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-100 dark:border-red-800">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">Delete Project</h4>
                    <p className="text-sm text-red-600 dark:text-red-400/80 mb-4">
                      Once you delete a project, there is no going back. Please be certain.
                    </p>
                    <button
                      onClick={handleDeleteProject}
                      className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm"
                    >
                      Delete Project
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedTask && (
        <TaskDetailModal
          isOpen={true}
          onClose={() => setSelectedTask(null)}
          task={selectedTask}
          projectId={project.id}
        />
      )}

      {showMilestoneModal && (
        <AddMilestoneModal
          isOpen={true}
          onClose={() => setShowMilestoneModal(false)}
          projectId={project.id}
        />
      )}

      {showAssetPicker && (
        <AssetLibraryPicker
          isOpen={true}
          onClose={() => {
            setShowAssetPicker(false);
            setAssetPickerContext(null);
            setSelectedTaskForAsset(null);
          }}
          onSelect={(assetId, asset) => handleLinkAsset(assetId, asset)}
        />
      )}

      {taskForAssignment && (
        <TaskAssignmentModal
          isOpen={true}
          onClose={() => setTaskForAssignment(null)}
          task={taskForAssignment}
        />
      )}
    </div>
  );
};

export default ViewProjectModal;
